import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import ParentModal from 'dpl/dog_modals/parent_modal/components/ParentModal';

import Icon from './Icon';
import TooltipWrapper from '../tooltip/components/TooltipWrapper';

const IS_LT_MD = isLessThanBreakpoint('md');

const HEALTH_BADGE_SIZE = IS_LT_MD ? '20px' : '24px';
const MAX_HEALTH_TESTS_TO_SHOW = 2;

function getParentHealthTestingDescription(healthTests) {
  const remainingTestCount = healthTests.length - MAX_HEALTH_TESTS_TO_SHOW;
  const healthTestsToDisplay = healthTests
    .slice(0, MAX_HEALTH_TESTS_TO_SHOW)
    .join(', ');
  return `${healthTestsToDisplay}${
    remainingTestCount > 0 ? `, +${remainingTestCount} more` : ''
  }.`;
}

// version 2: used on breeder profile page
export default function ParentHealthTestingBanner({
  className,
  healthTesting,
  hasPublicHealthTestResults,
  parentId
}) {
  const [activeParentId, setActiveParentId] = useState(null);

  const hasHealthTests = healthTesting.documented_health_tests?.length > 0;
  const showHealthTestingDescription =
    hasHealthTests && hasPublicHealthTestResults;

  return (
    <div
      className={classnames(
        'ParentHealthTestingBanner bg-teal-light ba b--light-gray br3 ph4 pv3 pv5-md',
        className
      )}
    >
      <div className="flex gap-16 items-center">
        <div className="ParentHealthTestingBanner__icon bg-white br-100">
          <Icon
            name="fetch-pets-heart"
            className="green-dark-700"
            width={HEALTH_BADGE_SIZE}
            height={HEALTH_BADGE_SIZE}
          />
        </div>
        <div
          className={classnames(
            'flex flex-column flex-lg-row flex-auto justify-between-ns font-14 md:font-16',
            { 'items-center-l': !hasHealthTests }
          )}
        >
          <div className="flex flex-column gap-2 md:gap-4">
            <div className="flex items-center gap-8">
              <p className="fw-medium forest-dark-900">{healthTesting.title}</p>
              <div className="dn flex-ns">
                <TooltipWrapper
                  minWidth="220px"
                  title={
                    <p>
                      Health testing is one key piece of responsible breeding
                      and is performed on breeding dogs to prevent the presence
                      of heritable conditions in their puppies.
                    </p>
                  }
                >
                  <Icon
                    className="stone-700"
                    name="fetch-info"
                    width="20px"
                    height="20px"
                  />
                </TooltipWrapper>
              </div>
            </div>
            <div
              className={classnames({
                'dn-ns': !showHealthTestingDescription
              })}
            >
              {showHealthTestingDescription && (
                <span className="secondary">
                  {getParentHealthTestingDescription(
                    healthTesting.documented_health_tests
                  )}{' '}
                </span>
              )}
              <button
                type="button"
                onClick={() => setActiveParentId(parentId)}
                className="dib dn-ns stone-700 underline"
              >
                View details
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActiveParentId(parentId)}
            className={classnames('nowrap dn db-ns stone-700 underline', {
              'self-start': showHealthTestingDescription
            })}
          >
            View details
          </button>
        </div>
      </div>
      <ParentModal
        dogId={activeParentId}
        isOpen={Boolean(activeParentId)}
        onClose={() => setActiveParentId(null)}
      />
    </div>
  );
}

ParentHealthTestingBanner.propTypes = {
  className: PropTypes.string,
  healthTesting: PropTypes.shape({
    title: PropTypes.string,
    documented_health_tests: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  hasPublicHealthTestResults: PropTypes.bool.isRequired,
  parentId: PropTypes.number.isRequired
};

ParentHealthTestingBanner.defaultProps = {
  className: ''
};
